import React, { Component } from 'react';
import { Helmet } from 'react-helmet';

import {
  listenToAuthState,
  normaliseUser,
  upsertUserProfile,
  signInWithGoogle,
  signInWithEmail,
  signUpWithEmail,
  logOut,
  listenToPosts,
  createPost,
  toggleVote,
  deletePost,
  listenToComments,
  addComment,
  listenToChat,
  sendChatMessage,
  uploadPostImage,
  uploadCommentImage,
  sendPhoneOtp,
  verifyPhoneOtp,
  resendVerificationEmail,
} from './ForumFirebase/firebase';

// ─────────────────────────────────────────────
//  STATIC DATA
// ─────────────────────────────────────────────
const POPULAR_TAGS = [
  { name: '#medicine',     posts: '45,678', trending: true  },
  { name: '#nursing',      posts: '38,912', trending: false },
  { name: '#healthtech',   posts: '52,134', trending: true  },
  { name: '#mentalhealth', posts: '41,567', trending: false },
  { name: '#vaccines',     posts: '49,823', trending: true  },
  { name: '#telemedicine', posts: '55,291', trending: false },
];

const PINNED_GROUPS = ['medicine', 'nursing', 'healthtech'];

const SPECIALISTS = [
  { initials: 'KR', name: 'Dr. Kavitha Rao',  spec: 'Cardiologist · MBBS, MD',     bg: '#DBEAFE', col: '#1D4ED8', online: true  },
  { initials: 'SM', name: 'Dr. Suresh M.',     spec: 'Endocrinologist',              bg: '#EDE9FE', col: '#6D28D9', online: true  },
  { initials: 'LN', name: 'Dr. Lakshmi N.',    spec: 'Clinical Nutritionist, PhD',  bg: '#DCFCE7', col: '#166534', online: false },
  { initials: 'RP', name: 'Dr. Rajan P.',      spec: 'General Physician',           bg: '#E0F2FE', col: '#0369A1', online: true  },
  { initials: 'VN', name: 'Dr. Veena N.',      spec: 'Gynaecologist & Obstetrician',bg: '#FFE4E6', col: '#BE123C', online: true  },
];

const STATS = [
  { num: '12.4k', label: 'Members'     },
  { num: '2,401', label: 'Threads'     },
  { num: '148',   label: 'Specialists' },
  { num: '94%',   label: 'Answered'    },
];

const MEETUP_TITLES = [
  'Live Webinar: Advances in AI for Healthcare Diagnostics',
  'Virtual Panel: Telemedicine Trends in 2025',
  'Online Workshop: Mental Health Strategies for Clinicians',
  'Webinar: Vaccine Development and Distribution',
  'Live Discussion: EHR Systems and Patient Privacy',
];

const MEETUP_LOCATIONS = [
  'Virtual Event • Hosted by HealthTech Institute',
  'Online • Sponsored by MedEd Foundation',
  'Webinar • Global Health Network',
  'Virtual Conference • Healthcare Innovators',
  'Live Stream • Nursing Association',
];

const CATEGORIES = [
  'All', 'General Health', 'Chronic Conditions', 'Mental Wellness',
  'Nutrition & Diet', 'Preventive Care', "Women's Health",
  'Emergency Info', 'Child Health', 'Senior Care',
];

const CAT_COLORS = {
  'All': '#3B82F6', 'General Health': '#0EA5E9', 'Chronic Conditions': '#6366F1',
  'Mental Wellness': '#8B5CF6', 'Nutrition & Diet': '#06B6D4', 'Preventive Care': '#10B981',
  "Women's Health": '#EC4899', 'Emergency Info': '#EF4444',
  'Child Health': '#1D4ED8', 'Senior Care': '#64748B',
};

// ─────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────
function timeNow() {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function dateOffset(n = 0) {
  return new Date(Date.now() + n * 86400000)
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    .toUpperCase();
}

function timeAgo(ts) {
  if (!ts) return '';
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const d = Math.floor(diff / 86400);
  return d === 1 ? 'Yesterday' : `${d} days ago`;
}

function catBadgeClass(cat) {
  const map = {
    'General Health': 'hf__badge-b', 'Chronic Conditions': 'hf__badge-i',
    'Mental Wellness': 'hf__badge-vl', 'Nutrition & Diet': 'hf__badge-c',
    'Preventive Care': 'hf__badge-t', "Women's Health": 'hf__badge-r',
    'Emergency Info': 'hf__badge-r', 'Child Health': 'hf__badge-s',
    'Senior Care': 'hf__badge-sl',
  };
  return map[cat] || 'hf__badge-sl';
}

function rolePillClass(role) {
  if (role === 'Doctor')     return 'hf__rpill-doc';
  if (role === 'Caregiver')  return 'hf__rpill-care';
  if (role === 'Researcher') return 'hf__rpill-res';
  return 'hf__rpill-pat';
}

// ── Translate Firebase error codes to readable messages ──────────
function firebaseErrorMessage(error) {
  const code = error?.code || '';
  const map = {
    'auth/user-not-found':       'No account found with this email address.',
    'auth/wrong-password':       'Incorrect password. Please try again.',
    'auth/invalid-email':        'The email address is not valid.',
    'auth/email-already-in-use': 'An account with this email already exists. Please sign in.',
    'auth/weak-password':        'Password is too weak. Use at least 6 characters.',
    'auth/too-many-requests':    'Too many attempts. Please wait a moment and try again.',
    'auth/network-request-failed': 'Network error. Check your internet connection.',
    'auth/invalid-credential':   'Incorrect email or password. Please try again.',
    'auth/user-disabled':        'This account has been disabled. Contact support.',
    'auth/popup-closed-by-user': 'Google sign-in was cancelled.',
    'auth/cancelled-popup-request': 'Google sign-in was cancelled.',
  };
  if (code === 'EMAIL_NOT_VERIFIED') {
    return 'Please verify your email first. Check your inbox for the verification link.';
  }
  return map[code] || error?.message || 'Something went wrong. Please try again.';
}

// ─────────────────────────────────────────────
//  SEO COMPONENT
// ─────────────────────────────────────────────
function ForumSEO({ post }) {
  const base        = 'https://klinicq.com';
  const forumUrl    = `${base}/forum`;
  const title       = post
    ? `${post.title} | KlinicQ Forum`
    : 'KlinicQ Healthcare Forum — Ask Doctors & Health Experts';
  const description = post
    ? `${post.body?.slice(0, 155)}…`
    : 'Ask real health questions, get answers from verified doctors and specialists. Join 12,000+ members discussing General Health, Chronic Conditions, Mental Wellness and more.';
  const canonical   = post ? `${forumUrl}/post/${post.id}` : forumUrl;
  const image       = post?.imageUrl || `${base}/og-forum.png`;
  const ld          = post ? {
    "@context": "https://schema.org", "@type": "DiscussionForumPosting",
    headline: post.title, text: post.body,
    author: { "@type": "Person", name: post.author },
    datePublished: post.ts?.toDate ? post.ts.toDate().toISOString() : new Date().toISOString(),
    url: canonical,
  } : {
    "@context": "https://schema.org", "@type": "WebPage",
    name: "KlinicQ Healthcare Forum", description, url: forumUrl,
    publisher: { "@type": "Organization", name: "KlinicQ", logo: { "@type": "ImageObject", url: `${base}/logo.webp` } },
    breadcrumb: { "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home",  item: base     },
      { "@type": "ListItem", position: 2, name: "Forum", item: forumUrl },
    ]},
  };
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description"         content={description} />
      <meta name="keywords"            content="healthcare forum, ask a doctor, medical questions, health community, KlinicQ, telemedicine, mental health, chronic conditions" />
      <meta name="robots"              content="index, follow" />
      <link rel="canonical"            href={canonical} />
      <meta property="og:type"         content={post ? "article" : "website"} />
      <meta property="og:title"        content={title} />
      <meta property="og:description"  content={description} />
      <meta property="og:url"          content={canonical} />
      <meta property="og:image"        content={image} />
      <meta property="og:site_name"    content="KlinicQ" />
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={image} />
      <script type="application/ld+json">{JSON.stringify(ld)}</script>
    </Helmet>
  );
}

// ─────────────────────────────────────────────
//  LOGIN SCREEN
//  Changes vs original:
//   1. Gmail login KEPT exactly as before
//   2. Email login now surfaces Firebase error messages properly
//   3. Email signup sends OTP (verification email) then shows
//      a waiting screen — user verifies, comes back and signs in
//   4. Phone OTP tab added (new)
// ─────────────────────────────────────────────
function LoginScreen({ onGoogleLogin, onEmailLogin, onEmailSignup }) {
  // 'main' | 'email' | 'phone'
  const [loginMode,   setLoginMode]   = React.useState('main');
  const [isSignup,    setIsSignup]    = React.useState(false);

  // email fields
  const [emailInput,  setEmailInput]  = React.useState('');
  const [passInput,   setPassInput]   = React.useState('');
  const [nameInput,   setNameInput]   = React.useState('');

  // signup OTP waiting state
  // After signUpWithEmail the user must click the link in their inbox.
  // We show a "check your email" screen until they confirm and come back.
  const [signupPending,      setSignupPending]      = React.useState(false);
  const [signupEmail,        setSignupEmail]        = React.useState('');
  const [signupPass,         setSignupPass]         = React.useState('');
  const [resendLoading,      setResendLoading]      = React.useState(false);
  const [resendInfo,         setResendInfo]         = React.useState('');

  // phone fields
  const [phoneInput,   setPhoneInput]   = React.useState('');
  const [otpInput,     setOtpInput]     = React.useState('');
  const [otpSent,      setOtpSent]      = React.useState(false);

  const [error,   setError]   = React.useState('');
  const [info,    setInfo]    = React.useState('');
  const [loading, setLoading] = React.useState(false);

  function resetAll() {
    setError(''); setInfo('');
    setEmailInput(''); setPassInput(''); setNameInput('');
    setSignupPending(false); setSignupEmail(''); setSignupPass('');
    setPhoneInput(''); setOtpInput(''); setOtpSent(false);
    setResendInfo('');
  }

  // ── Google ────────────────────────────────
  async function handleGoogleClick() {
    setLoading(true); setError('');
    try {
      await onGoogleLogin();
    } catch (e) {
      setError(firebaseErrorMessage(e));
    }
    setLoading(false);
  }

  // ── Email sign-in ─────────────────────────
  async function handleEmailSignIn() {
    setError(''); setInfo('');
    if (!emailInput.includes('@')) { setError('Enter a valid email address.'); return; }
    if (passInput.length < 6)      { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      await onEmailLogin(emailInput, passInput);
      // onAuthStateChanged in ForumLayout handles the rest
    } catch (e) {
      // Show the exact Firebase error translated to plain English
      setError(firebaseErrorMessage(e));
    }
    setLoading(false);
  }

  // ── Email sign-up + send OTP to email ─────
  // Firebase's "OTP" for email = verification link sent to inbox.
  // signUpWithEmail() in firebase.js:
  //   1. Creates the account
  //   2. Sends the verification email (this IS the OTP/link)
  //   3. Signs the user out so they must verify first
  // We then show a "check your email" waiting screen.
  async function handleEmailSignUp() {
    setError(''); setInfo('');
    if (!nameInput.trim())         { setError('Please enter your name.'); return; }
    if (!emailInput.includes('@')) { setError('Enter a valid email address.'); return; }
    if (passInput.length < 6)      { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      await onEmailSignup(emailInput, passInput, nameInput.trim());
      // Show waiting screen — verification email (OTP link) has been sent
      setSignupEmail(emailInput);
      setSignupPass(passInput);
      setSignupPending(true);
      setInfo('');
    } catch (e) {
      setError(firebaseErrorMessage(e));
    }
    setLoading(false);
  }

  // ── Resend verification email ──────────────
  async function handleResendVerification() {
    setResendLoading(true); setResendInfo('');
    try {
      await resendVerificationEmail(signupEmail, signupPass);
      setResendInfo('Verification email resent! Check your inbox.');
    } catch (e) {
      setResendInfo('Failed to resend. Please try again.');
    }
    setResendLoading(false);
  }

  // ── After user verifies email — sign them in ──
  async function handleSignInAfterVerification() {
    setError(''); setInfo('');
    setLoading(true);
    try {
      await onEmailLogin(signupEmail, signupPass);
    } catch (e) {
      setError(firebaseErrorMessage(e));
    }
    setLoading(false);
  }

  // ── Phone: send OTP via SMS ───────────────
 async function handleSendPhoneOtp() {
  setError(''); setInfo('');
 
  // phoneInput is bare 10 digits (e.g. "9894178090")
  const fullPhone = '+91' + phoneInput.replace(/^\+?91/, '');
 
  if (!/^\+91[6-9]\d{9}$/.test(fullPhone)) {
    setError('Enter a valid 10-digit Indian mobile number (starts with 6–9).');
    return;
  }
 
  setLoading(true);
  try {
    await sendPhoneOtp(fullPhone, 'recaptcha-container');
    setPhoneInput(fullPhone);   // NOW store full number (only for display in step 2)
    setOtpSent(true);
    setInfo('OTP sent! Check your SMS inbox.');
  } catch (e) {
    setError('Failed to send OTP. Please try again.');
    console.error(e);
  }
  setLoading(false);
}

  // ── Phone: verify OTP ─────────────────────
  async function handleVerifyPhoneOtp() {
    setError(''); setInfo('');
    if (otpInput.length !== 6) { setError('Enter the 6-digit OTP from your SMS.'); return; }
    setLoading(true);
    try {
      await verifyPhoneOtp(otpInput);
      // onAuthStateChanged in ForumLayout will fire automatically
    } catch (e) {
      setError('Invalid OTP. Please try again.');
    }
    setLoading(false);
  }

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    .ls__root{min-height:100vh;background:linear-gradient(135deg,#EFF6FF 0%,#DBEAFE 40%,#EDE9FE 100%);display:flex;align-items:center;justify-content:center;font-family:'Inter',sans-serif;padding:20px;}
    .ls__card{background:#fff;border-radius:24px;box-shadow:0 20px 60px rgba(37,99,235,0.13);width:100%;max-width:420px;overflow:hidden;}
    .ls__hero{padding:36px 32px 28px;text-align:center;}
    .ls__body{padding:28px 32px 32px;}
    .ls__gbtn{width:100%;padding:13px;border-radius:12px;border:1.5px solid #E2E8F0;background:#fff;display:flex;align-items:center;justify-content:center;gap:10px;font-family:'Inter',sans-serif;font-size:14px;font-weight:500;color:#1E293B;cursor:pointer;transition:all .15s;margin-bottom:10px;}
    .ls__gbtn:hover{background:#F8FAFC;border-color:#3B82F6;box-shadow:0 2px 10px rgba(59,130,246,.12);}
    .ls__gbtn:disabled{opacity:.6;cursor:not-allowed;}
    .ls__div{display:flex;align-items:center;gap:10px;margin:14px 0;}
    .ls__div-line{flex:1;height:1px;background:#E2E8F0;}
    .ls__div-txt{font-size:12px;color:#94A3B8;}
    .ls__field{margin-bottom:14px;}
    .ls__label{display:block;font-size:12px;font-weight:600;color:#475569;margin-bottom:5px;}
    .ls__input{width:100%;padding:11px 14px;border-radius:10px;border:1.5px solid #E2E8F0;font-family:'Inter',sans-serif;font-size:14px;color:#1E293B;background:#F8FAFC;box-sizing:border-box;outline:none;transition:all .15s;}
    .ls__input:focus{border-color:#3B82F6;background:#fff;box-shadow:0 0 0 3px rgba(59,130,246,.1);}
    .ls__input-err{border-color:#FCA5A5 !important;background:#FFF5F5 !important;}
    .ls__submit{width:100%;padding:13px;border-radius:12px;border:none;background:linear-gradient(135deg,#1D4ED8,#3B82F6);color:#fff;font-family:'Inter',sans-serif;font-size:15px;font-weight:600;cursor:pointer;transition:all .2s;margin-top:4px;}
    .ls__submit:hover{opacity:.92;transform:translateY(-1px);}
    .ls__submit:disabled{opacity:.6;cursor:not-allowed;transform:none;}
    .ls__back{background:none;border:none;color:#3B82F6;font-family:'Inter',sans-serif;font-size:13px;cursor:pointer;padding:0;margin-bottom:18px;display:flex;align-items:center;gap:5px;}
    .ls__toggle{text-align:center;margin-top:12px;font-size:13px;color:#64748B;}
    .ls__toggle button{background:none;border:none;color:#3B82F6;font-weight:600;cursor:pointer;font-family:'Inter',sans-serif;font-size:13px;}
    .ls__err{font-size:12px;color:#DC2626;margin-top:12px;text-align:center;padding:10px 12px;background:#FEF2F2;border:1px solid #FECACA;border-radius:10px;line-height:1.5;}
    .ls__info{font-size:12px;color:#059669;margin-top:12px;text-align:center;padding:10px 12px;background:#ECFDF5;border:1px solid #A7F3D0;border-radius:10px;line-height:1.5;}
    .ls__stats{display:flex;gap:0;border-top:1px solid #F1F5F9;margin-top:8px;}
    .ls__stat{flex:1;text-align:center;padding:14px 8px;}
    .ls__stat-num{font-size:16px;font-weight:700;color:#1E40AF;}
    .ls__stat-lbl{font-size:11px;color:#94A3B8;margin-top:2px;}
    .ls__tabs{display:flex;gap:6px;margin-bottom:18px;background:#F8FAFC;border-radius:12px;padding:4px;}
    .ls__tab{flex:1;padding:8px;border-radius:9px;border:none;background:transparent;font-family:'Inter',sans-serif;font-size:13px;font-weight:500;color:#64748B;cursor:pointer;transition:all .15s;}
    .ls__tab.active{background:#fff;color:#1D4ED8;box-shadow:0 1px 4px rgba(0,0,0,.08);}
    .ls__otp-hint{font-size:11px;color:#94A3B8;margin-top:5px;}
    .ls__verify-box{text-align:center;padding:8px 0;}
    .ls__verify-icon{font-size:40px;margin-bottom:12px;}
    .ls__verify-title{font-size:16px;font-weight:700;color:#1E293B;margin-bottom:8px;}
    .ls__verify-desc{font-size:13px;color:#64748B;line-height:1.6;margin-bottom:16px;}
    .ls__verify-email{font-weight:600;color:#1D4ED8;}
    .ls__link-btn{background:none;border:none;color:#3B82F6;font-weight:600;cursor:pointer;font-family:'Inter',sans-serif;font-size:13px;text-decoration:underline;}
    .ls__link-btn:disabled{opacity:.5;cursor:not-allowed;}
  `;

  return (
    <div className="ls__root">
      <style>{styles}</style>

      {/* REQUIRED for Firebase phone OTP — stays invisible */}
      <div id="recaptcha-container" />

      <div className="ls__card">
        <div className="ls__hero">
          <a className="navbar-brand klinic-logo" href="/">
            <img
              src={require("../../src/assets/images/website/logo.webp")}
              alt="Klinicq Logo"
              className="nav-logo"
              fetchPriority="high"
              loading="eager"
              decoding="sync"
            />
          </a>
        </div>

        <div className="ls__body">

          {/* ════════════════════════════════
              MAIN SCREEN
          ════════════════════════════════ */}
          {loginMode === 'main' && (
            <>
              {/* Google / Gmail — unchanged from original */}
              <button className="ls__gbtn" onClick={handleGoogleClick} disabled={loading}>
                <svg width="18" height="18" viewBox="0 0 18 18">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"/>
                  <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z"/>
                </svg>
                {loading ? 'Signing in…' : 'Continue with Google / Gmail'}
              </button>

              <div className="ls__div">
                <div className="ls__div-line"/><span className="ls__div-txt">or</span><div className="ls__div-line"/>
              </div>

              {/* Phone OTP */}
              {/* <button className="ls__gbtn" onClick={() => { resetAll(); setLoginMode('phone'); }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                  <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.58.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1C11.4 21 3 12.6 3 4c0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.23 1.01L6.6 10.8z" stroke="#1D4ED8" strokeWidth="1.5" fill="none"/>
                </svg>
                Sign in with Phone (OTP via SMS)
              </button> */}

              {/* Email / Password */}
              <button className="ls__gbtn" onClick={() => { resetAll(); setLoginMode('email'); }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect x="1" y="3" width="14" height="10" rx="2" stroke="#64748B" strokeWidth="1.4"/>
                  <path d="M1 5l7 5 7-5" stroke="#64748B" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
                Sign in with Email / Password
              </button>

              <div className="ls__stats">
                {STATS.map(s => (
                  <div className="ls__stat" key={s.label}>
                    <div className="ls__stat-num">{s.num}</div>
                    <div className="ls__stat-lbl">{s.label}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ════════════════════════════════
              PHONE OTP SCREEN (new)
          ════════════════════════════════ */}
   


{loginMode === 'phone' && (
  <>
    <button className="ls__back" onClick={() => { resetAll(); setLoginMode('main'); }}>← Back</button>

    
    {!otpSent ? (
      <>
        <div className="ls__field">
          <label className="ls__label">Mobile Number (India)</label>

          {/* Combined prefix + input row */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            border: '1.5px solid #E2E8F0',
            borderRadius: 10,
            background: '#F8FAFC',
            overflow: 'hidden',
            transition: 'all .15s',
          }}
            onFocusCapture={e => {
              e.currentTarget.style.borderColor = '#3B82F6';
              e.currentTarget.style.background  = '#fff';
              e.currentTarget.style.boxShadow   = '0 0 0 3px rgba(59,130,246,.1)';
            }}
            onBlurCapture={e => {
              e.currentTarget.style.borderColor = '#E2E8F0';
              e.currentTarget.style.background  = '#F8FAFC';
              e.currentTarget.style.boxShadow   = 'none';
            }}
          >
            {/* +91 badge */}
            <div style={{
              padding: '11px 12px',
              borderRight: '1.5px solid #E2E8F0',
              fontFamily: "'Inter', sans-serif",
              fontSize: 14,
              fontWeight: 600,
              color: '#1D4ED8',
              background: '#EFF6FF',
              whiteSpace: 'nowrap',
              userSelect: 'none',
              flexShrink: 0,
            }}>
              🇮🇳 +91
            </div>

            {/* 10-digit number input */}
            <input
              style={{
                flex: 1,
                padding: '11px 14px',
                border: 'none',
                outline: 'none',
                fontFamily: "'Inter', sans-serif",
                fontSize: 14,
                color: '#1E293B',
                background: 'transparent',
                letterSpacing: '0.05em',
              }}
              type="tel"
              inputMode="numeric"
              maxLength={10}
              placeholder="98765 43210"
              value={phoneInput}
              onChange={e => {
  let raw = e.target.value;
 
  // Strip any leading +91, 091, or 91 the user may have typed/pasted
  raw = raw.replace(/^\+?91/, '');   // removes +91 or 91 at the start
  raw = raw.replace(/^\+/, '');      // removes lone + if left
 
  // Keep only digits, max 10
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  setPhoneInput(digits);
  setError('');
}}
              onKeyDown={e => e.key === 'Enter' && handleSendPhoneOtp()}
            />

            {/* Live digit counter */}
            <div style={{
              padding: '11px 12px',
              fontFamily: "'Inter', sans-serif",
              fontSize: 12,
              color: phoneInput.length === 10 ? '#059669' : '#94A3B8',
              fontWeight: phoneInput.length === 10 ? 600 : 400,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}>
              {phoneInput.length}/10
            </div>
          </div>

          {/* Helper hint */}
          <p style={{
            fontSize: 11,
            color: '#94A3B8',
            marginTop: 6,
            lineHeight: 1.5,
          }}>
            Enter your 10-digit Indian mobile number. We'll send an OTP via SMS.
          </p>
        </div>

        {/* Preview of full number */}
        {phoneInput.length > 0 && (
          <div style={{
            background: '#F0FDF4',
            border: '1px solid #BBF7D0',
            borderRadius: 8,
            padding: '8px 12px',
            marginBottom: 14,
            fontSize: 13,
            color: '#166534',
            fontFamily: "'Inter', sans-serif",
          }}>
            OTP will be sent to: <strong>+91 {phoneInput}</strong>
          </div>
        )}

        <button
          className="ls__submit"
         onClick={() => {
  if (phoneInput.length !== 10) {
    setError('Please enter a valid 10-digit mobile number.');
    return;
  }
  if (!/^[6-9]/.test(phoneInput)) {
    setError('Indian mobile numbers must start with 6, 7, 8, or 9.');
    return;
  }
  // phoneInput is always bare 10 digits here — handleSendPhoneOtp adds +91
  handleSendPhoneOtp();
}}
          disabled={loading || phoneInput.length !== 10}
          style={{ opacity: phoneInput.length !== 10 ? 0.6 : 1 }}
        >
          {loading ? 'Sending OTP…' : 'Send OTP via SMS'}
        </button>
      </>
    ) : (

      /* ── STEP 2: Enter the 6-digit OTP from SMS ── */
      <>
        {/* Show which number the OTP was sent to */}
        <div style={{
          background: '#EFF6FF',
          border: '1px solid #BFDBFE',
          borderRadius: 10,
          padding: '12px 14px',
          marginBottom: 16,
          fontFamily: "'Inter', sans-serif",
        }}>
          <div style={{ fontSize: 12, color: '#3B82F6', fontWeight: 600, marginBottom: 2 }}>
            OTP sent to
          </div>
          <div style={{ fontSize: 15, color: '#1E293B', fontWeight: 700 }}>
            {/* Display formatted: +91 XXXXX XXXXX */}
            {phoneInput.startsWith('+91')
              ? '+91 ' + phoneInput.slice(3, 8) + ' ' + phoneInput.slice(8)
              : phoneInput}
          </div>
          <div style={{ fontSize: 11, color: '#64748B', marginTop: 3 }}>
            Check your SMS inbox. OTP expires in 10 minutes.
          </div>
        </div>

        <div className="ls__field">
          <label className="ls__label">6-digit OTP</label>
          <input
            className="ls__input"
            style={{ letterSpacing: '0.3em', fontSize: 20, textAlign: 'center', fontWeight: 700 }}
            type="tel"
            inputMode="numeric"
            maxLength={6}
            placeholder="• • • • • •"
            value={otpInput}
            onChange={e => {
              const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
              setOtpInput(digits);
              setError('');
            }}
            onKeyDown={e => e.key === 'Enter' && otpInput.length === 6 && handleVerifyPhoneOtp()}
            autoFocus
          />
          <p style={{ fontSize: 11, color: '#94A3B8', marginTop: 5 }}>
            Enter the 6-digit code sent via SMS
          </p>
        </div>

        <button
          className="ls__submit"
          onClick={handleVerifyPhoneOtp}
          disabled={loading || otpInput.length !== 6}
          style={{ opacity: otpInput.length !== 6 ? 0.6 : 1 }}
        >
          {loading ? 'Verifying…' : 'Verify & Sign In'}
        </button>

        {/* Wrong number — go back */}
        <div className="ls__toggle" style={{ marginTop: 12 }}>
          Wrong number?{' '}
          <button onClick={() => {
            setOtpSent(false);
            setOtpInput('');
            // Reset to bare digits (strip +91) for re-entry
            setPhoneInput(prev => prev.startsWith('+91') ? prev.slice(3) : prev);
            setError('');
            setInfo('');
          }}>
            Change number
          </button>
        </div>

        {/* Resend OTP */}
        <div className="ls__toggle">
          Didn't receive it?{' '}
          <button
            disabled={loading}
            onClick={() => {
              handleSendPhoneOtp();
              setOtpInput('');
            }}
          >
            {loading ? 'Sending…' : 'Resend OTP'}
          </button>
        </div>
      </>
    )}
  </>
)}

          {/* ════════════════════════════════
              EMAIL SCREEN
              — Sign In: shows Firebase errors properly
              — Sign Up: sends OTP (verification email) then waits
          ════════════════════════════════ */}
          {loginMode === 'email' && !signupPending && (
            <>
              <button className="ls__back" onClick={() => { resetAll(); setLoginMode('main'); }}>← Back</button>

              {/* Sign In / Sign Up tabs */}
              <div className="ls__tabs">
                <button
                  className={`ls__tab${!isSignup ? ' active' : ''}`}
                  onClick={() => { setIsSignup(false); setError(''); setInfo(''); }}
                >
                  Sign In
                </button>
                <button
                  className={`ls__tab${isSignup ? ' active' : ''}`}
                  onClick={() => { setIsSignup(true); setError(''); setInfo(''); }}
                >
                  Sign Up
                </button>
              </div>

              {/* Name — only for signup */}
              {isSignup && (
                <div className="ls__field">
                  <label className="ls__label">Full Name</label>
                  <input
                    className="ls__input"
                    placeholder="Dr. Priya Sharma"
                    value={nameInput}
                    onChange={e => setNameInput(e.target.value)}
                  />
                </div>
              )}

              <div className="ls__field">
                <label className="ls__label">Email Address</label>
                <input
                  className={`ls__input${error ? ' ls__input-err' : ''}`}
                  type="email"
                  placeholder="you@example.com"
                  value={emailInput}
                  onChange={e => { setEmailInput(e.target.value); setError(''); }}
                />
              </div>

              <div className="ls__field">
                <label className="ls__label">Password</label>
                <input
                  className={`ls__input${error ? ' ls__input-err' : ''}`}
                  type="password"
                  placeholder={isSignup ? 'Create a password (min 6 chars)' : 'Your password'}
                  value={passInput}
                  onChange={e => { setPassInput(e.target.value); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && (isSignup ? handleEmailSignUp() : handleEmailSignIn())}
                />
              </div>

              <button
                className="ls__submit"
                onClick={isSignup ? handleEmailSignUp : handleEmailSignIn}
                disabled={loading}
              >
                {loading
                  ? (isSignup ? 'Creating account…' : 'Signing in…')
                  : (isSignup ? 'Create Account & Send Verification Email' : 'Sign In')}
              </button>
            </>
          )}

          {/* ════════════════════════════════
              EMAIL VERIFICATION WAITING SCREEN
              Shown after successful signup —
              user must click the link in their
              inbox (Firebase's email "OTP")
          ════════════════════════════════ */}
          {loginMode === 'email' && signupPending && (
            <>
              <div className="ls__verify-box">
                <div className="ls__verify-icon">📧</div>
                <div className="ls__verify-title">Check your email!</div>
                <div className="ls__verify-desc">
                  We sent a verification link to{' '}
                  <span className="ls__verify-email">{signupEmail}</span>.
                  <br /><br />
                  Click the link in the email to verify your account, then come back and click the button below to sign in.
                </div>

                <button
                  className="ls__submit"
                  onClick={handleSignInAfterVerification}
                  disabled={loading}
                  style={{ marginBottom: 12 }}
                >
                  {loading ? 'Signing in…' : "I've verified — Sign me in"}
                </button>

                <div style={{ fontSize: 13, color: '#64748B' }}>
                  Didn't receive the email?{' '}
                  <button
                    className="ls__link-btn"
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                  >
                    {resendLoading ? 'Sending…' : 'Resend verification email'}
                  </button>
                </div>

                {resendInfo && (
                  <div className="ls__info" style={{ marginTop: 10 }}>{resendInfo}</div>
                )}

                <div style={{ marginTop: 14, fontSize: 13, color: '#64748B' }}>
                  Wrong email?{' '}
                  <button
                    className="ls__link-btn"
                    onClick={() => { resetAll(); setIsSignup(true); }}
                  >
                    Start over
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Error and info banners */}
          {error && <div className="ls__err">⚠ {error}</div>}
          {info  && !signupPending && <div className="ls__info">✓ {info}</div>}

        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════
//  MAIN CLASS COMPONENT  (unchanged from original)
// ═════════════════════════════════════════════
class ForumLayout extends Component {
  constructor(props) {
    super(props);
    this.state = {
      /* auth */
      loggedIn: false,
      user:     null,

      /* data from Firestore */
      posts:        [],
      chatMessages: [],

      /* ui filters */
      currentFilter: 'latest',
      activeCat:     'All',
      searchQuery:   '',

      /* new-post modal */
      showModal:    false,
      newPostTitle: '',
      newPostBody:  '',
      newPostTags:  '',
      newPostCat:   'General Health',
      newPostRole:  'Patient',
      newPostImage: null,
      newPostImageFile: null,
      postUploading: false,

      /* thread detail */
      detailPostId:   null,
      detailComments: [],
      replyText:      '',

      /* inline comment inputs — keyed by postId */
      commentInputs: {},

      /* live chat */
      chatInput:  '',
      chatTyping: false,

      /* meetups */
      meetups:        [],
      showAllMeetups: false,
    };

    this.chatBodyRef = React.createRef();

    this.unsubAuth     = null;
    this.unsubPosts    = null;
    this.unsubChat     = null;
    this.unsubComments = null;
    this.liveTimer     = null;
  }

  // ── LIFECYCLE ──────────────────────────────
  componentDidMount() {
    this.unsubAuth = listenToAuthState(async (firebaseUser) => {
      if (firebaseUser) {
        await upsertUserProfile(firebaseUser);
        this.setState({ loggedIn: true, user: normaliseUser(firebaseUser) });
        this.startFirestoreListeners();
      } else {
        this.setState({ loggedIn: false, user: null, posts: [], chatMessages: [] });
        this.stopFirestoreListeners();
      }
    });

    this.setState({
      meetups: [
        { date: dateOffset(0), title: 'AMA Annual Conference — Advancing Medicine',  location: 'American Medical Association • Chicago, IL' },
        { date: dateOffset(1), title: 'Nursing Leadership Summit | ANA',              location: 'American Nurses Association • New York, NY'  },
        { date: dateOffset(2), title: 'HealthTech Innovation Expo — HIMSS',           location: 'HIMSS • Orlando, FL'                         },
      ],
    });

    this.liveTimer = setInterval(this.addLiveMeetup, 10000);
  }

  componentWillUnmount() {
    this.unsubAuth     && this.unsubAuth();
    this.stopFirestoreListeners();
    clearInterval(this.liveTimer);
  }

  startFirestoreListeners = () => {
    this.unsubPosts = listenToPosts((posts) => {
      this.setState(prev => {
        const inputs = prev.commentInputs;
        return {
          posts: posts.map(p => ({
            ...p,
            liked:           Array.isArray(p.likedBy) && prev.user
                               ? p.likedBy.includes(prev.user.uid)
                               : false,
            showCommentInput: inputs[p.id]?.showCommentInput || false,
            commentText:      inputs[p.id]?.commentText || '',
            showShareMenu:    false,
            showReportMenu:   false,
          })),
        };
      });
    });

    this.unsubChat = listenToChat((messages) => {
      this.setState({ chatMessages: messages }, this.scrollChat);
    });
  };

  stopFirestoreListeners = () => {
    this.unsubPosts    && this.unsubPosts();
    this.unsubChat     && this.unsubChat();
    this.unsubComments && this.unsubComments();
    this.unsubPosts    = null;
    this.unsubChat     = null;
    this.unsubComments = null;
  };

  componentDidUpdate(_, prevState) {
    if (prevState.chatMessages.length !== this.state.chatMessages.length) {
      this.scrollChat();
    }
  }

  scrollChat = () => {
    if (this.chatBodyRef.current) {
      this.chatBodyRef.current.scrollTop = this.chatBodyRef.current.scrollHeight;
    }
  };

  // ── AUTH ───────────────────────────────────
  handleGoogleLogin = () => signInWithGoogle();
  handleEmailLogin  = (email, pass) => signInWithEmail(email, pass);
  handleEmailSignup = (email, pass, name) => signUpWithEmail(email, pass, name);
  handleLogout      = () => logOut();

  // ── MEETUPS ────────────────────────────────
  addLiveMeetup = () => {
    const title    = MEETUP_TITLES[Math.floor(Math.random() * MEETUP_TITLES.length)];
    const location = MEETUP_LOCATIONS[Math.floor(Math.random() * MEETUP_LOCATIONS.length)];
    this.setState(prev => ({
      meetups: [{ date: dateOffset(0), title, location }, ...prev.meetups],
    }));
  };

  // ── SEARCH / FILTER / CATEGORY ─────────────
  handleSearch = (e) => this.setState({ searchQuery: e.target.value });
  setFilter    = (f) => this.setState({ currentFilter: f });
  setCategory  = (cat) => this.setState({ activeCat: cat });

  getFilteredPosts() {
    const { posts, activeCat, currentFilter, searchQuery } = this.state;
    let result = posts.slice();

    if (activeCat !== 'All') result = result.filter(p => p.category === activeCat);

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p =>
        p.title?.toLowerCase().includes(q) ||
        p.body?.toLowerCase().includes(q)  ||
        p.author?.toLowerCase().includes(q)||
        (p.tags || []).join(' ').toLowerCase().includes(q)
      );
    }

    if (currentFilter === 'top') {
      result.sort((a, b) => (b.votes || 0) - (a.votes || 0));
    } else if (currentFilter === 'unanswered') {
      result = result.filter(p => !p.answered);
    } else {
      result.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        const ta = a.ts?.toDate ? a.ts.toDate().getTime() : (a.ts || 0);
        const tb = b.ts?.toDate ? b.ts.toDate().getTime() : (b.ts || 0);
        return tb - ta;
      });
    }
    return result;
  }

  // ── MODAL ──────────────────────────────────
  openModal  = () => this.setState({ showModal: true });
  closeModal = () => this.setState({
    showModal: false, newPostTitle: '', newPostBody: '',
    newPostTags: '', newPostImage: null, newPostImageFile: null,
  });

  handleNewPostImageChange = (file) => {
    if (!file) return;
    this.setState({ newPostImageFile: file });
    const reader = new FileReader();
    reader.onload = (e) => this.setState({ newPostImage: e.target.result });
    reader.readAsDataURL(file);
  };

  handleCreatePost = async () => {
    const { newPostTitle, newPostBody, newPostTags, newPostCat, newPostRole, newPostImageFile } = this.state;
    const { user } = this.state;
    if (!newPostTitle.trim()) { alert('Please enter a post title.'); return; }

    this.setState({ postUploading: true });
    try {
      let imageUrl = null;
      if (newPostImageFile) {
        imageUrl = await uploadPostImage(newPostImageFile, user.uid);
      }

      const tags = newPostTags.split(',').map(t => t.trim()).filter(Boolean);

      await createPost({
        title:    newPostTitle,
        body:     newPostBody,
        category: newPostCat,
        tags,
        role:     newPostRole,
        imageUrl,
        user,
      });

      this.closeModal();
    } catch (e) {
      alert('Failed to create post. Please try again.');
      console.error(e);
    }
    this.setState({ postUploading: false });
  };

  // ── VOTE ───────────────────────────────────
  handleVote = (postId) => {
    const { user, posts } = this.state;
    if (!user) return;
    const post = posts.find(p => p.id === postId);
    if (!post) return;
    toggleVote(postId, user.uid, post.liked);
  };

  // ── SHARE / REPORT ─────────────────────────
  toggleShareMenu = (id) => this.setState(prev => ({
    posts: prev.posts.map(p =>
      p.id === id ? { ...p, showShareMenu: !p.showShareMenu, showReportMenu: false } : p
    ),
  }));

  toggleReportMenu = (id) => this.setState(prev => ({
    posts: prev.posts.map(p =>
      p.id === id ? { ...p, showReportMenu: !p.showReportMenu, showShareMenu: false } : p
    ),
  }));

  handleCopyLink = (id) => {
    navigator.clipboard.writeText(`https://healthforum.com/post/${id}`)
      .then(() => alert('Link copied!'));
    this.setState(prev => ({
      posts: prev.posts.map(p => p.id === id ? { ...p, showShareMenu: false } : p),
    }));
  };

  handleReport = (id) => {
    alert('Post reported.');
    this.setState(prev => ({
      posts: prev.posts.map(p => p.id === id ? { ...p, showReportMenu: false } : p),
    }));
  };

  // ── COMMENTS (inline on post card) ─────────
  toggleCommentInput = (id) => {
    this.setState(prev => ({
      commentInputs: {
        ...prev.commentInputs,
        [id]: {
          ...prev.commentInputs[id],
          showCommentInput: !prev.commentInputs[id]?.showCommentInput,
        },
      },
      posts: prev.posts.map(p =>
        p.id === id ? { ...p, showCommentInput: !p.showCommentInput } : p
      ),
    }));
  };

  handleCommentChange = (id, text) => {
    this.setState(prev => ({
      commentInputs: { ...prev.commentInputs, [id]: { ...prev.commentInputs[id], commentText: text } },
      posts: prev.posts.map(p => p.id === id ? { ...p, commentText: text } : p),
    }));
  };

  handleAddComment = async (postId) => {
    const { user } = this.state;
    const post = this.state.posts.find(p => p.id === postId);
    if (!post?.commentText?.trim()) return;

    try {
      await addComment(postId, {
        text:     post.commentText,
        imageUrl: null,
        parentId: null,
        user,
      });
      this.handleCommentChange(postId, '');
    } catch (e) {
      console.error('Comment failed:', e);
    }
  };

  // ── THREAD DETAIL ──────────────────────────
  openDetail = (id) => {
    this.setState({ detailPostId: id, replyText: '', detailComments: [] });

    this.unsubComments && this.unsubComments();
    this.unsubComments = listenToComments(id, (comments) => {
      this.setState({ detailComments: comments });
    });
  };

  closeDetail = () => {
    this.setState({ detailPostId: null, detailComments: [] });
    this.unsubComments && this.unsubComments();
    this.unsubComments = null;
  };

  handleDetailVote  = () => this.handleVote(this.state.detailPostId);

  handleDetailReply = async () => {
    const { detailPostId, replyText, user } = this.state;
    if (!replyText.trim()) return;
    try {
      await addComment(detailPostId, {
        text: replyText, imageUrl: null, parentId: null, user,
      });
      this.setState({ replyText: '' });
    } catch (e) {
      console.error('Reply failed:', e);
    }
  };

  // ── CHAT ───────────────────────────────────
  handleChatInput   = (e) => this.setState({ chatInput: e.target.value });
  handleChatKeyDown = (e) => { if (e.key === 'Enter') this.handleChatSend(); };

  handleChatSend = async () => {
    const { chatInput, user } = this.state;
    if (!chatInput.trim()) return;
    this.setState({ chatInput: '' });
    try {
      await sendChatMessage(chatInput, user, false);
    } catch (e) {
      console.error('Chat send failed:', e);
    }
  };

  // ─────────────────────────────────────────────
  //  RENDER HELPERS
  // ─────────────────────────────────────────────
  renderComment(comment, allComments) {
    const replies = allComments.filter(c => c.parentId === comment.id);
    return (
      <div key={comment.id} className="hf__comment" style={{ marginLeft: comment.parentId ? 20 : 0 }}>
        <div className="hf__comment-meta">
          <span className="hf__comment-author">{comment.author}</span>
          <span className="hf__comment-date">{timeAgo(comment.ts)}</span>
        </div>
        {comment.text && <p className="hf__comment-text">{comment.text}</p>}
        {comment.imageUrl && <img src={comment.imageUrl} alt="attachment" className="hf__comment-img" />}
        {replies.map(r => this.renderComment(r, allComments))}
      </div>
    );
  }

  // ─────────────────────────────────────────────
  //  RENDER SECTIONS  (all unchanged from original)
  // ─────────────────────────────────────────────
  renderHeader() {
    return (
      <header className="hf__topbar">
        <Helmet>
          <title>KlinicQ Healthcare Forum | Ask Medical Questions</title>
          <meta name="description" content="Ask health questions, explore medical topics, and connect with experts in our healthcare forum." />
        </Helmet>

        <div className="hf__logo">
          <a className="navbar-brand klinic-logo" href="/">
            <img
              src={require("../../src/assets/images/website/logo.webp")}
              alt="Klinicq Logo"
              className="nav-logo"
              fetchPriority="high"
              loading="eager"
              decoding="sync"
            />
          </a>
        </div>

        <div className="hf__searchbox">
          <span className="hf__si">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
              <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.4" />
              <path d="M10.5 10.5L13.5 13.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          </span>
          <input
            type="text"
            placeholder="Search healthcare topics…"
            value={this.state.searchQuery}
            onChange={this.handleSearch}
          />
        </div>

        <nav className="hf__nav">
          {['Discussions', 'Questions', 'Specialists', 'Resources'].map(n => (
            <button key={n} className="hf__navbtn">{n}</button>
          ))}
        </nav>

        <div className="hf__actions">
          <button className="hf__btn-new" onClick={this.openModal}>
            <svg viewBox="0 0 13 13" fill="none">
              <path d="M6.5 1.5v10M1.5 6.5h10" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            New Post
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              className="hf__avatar-me"
              style={{ background: this.state.user?.bg, color: this.state.user?.col, cursor: 'default' }}
              title={this.state.user?.name}
            >
              {this.state.user?.initials || 'ME'}
            </div>
            <button
              onClick={this.handleLogout}
              style={{
                padding: '6px 12px', borderRadius: 8, border: '1.5px solid #E2E8F0',
                background: '#fff', cursor: 'pointer', fontSize: 12, color: '#64748B',
                fontFamily: 'inherit', fontWeight: 500, transition: 'all .15s',
              }}
              onMouseOver={e => { e.target.style.borderColor = '#EF4444'; e.target.style.color = '#EF4444'; }}
              onMouseOut={e  => { e.target.style.borderColor = '#E2E8F0'; e.target.style.color = '#64748B'; }}
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>
    );
  }

  renderLeftSidebar() {
    const { currentFilter, activeCat } = this.state;
    return (
      <aside className="hf__left">
        <div className="hf__card">
          <div className="hf__card-hd">Browse</div>
          <ul className="hf__filter-list">
            {[
              { key: 'latest',     icon: '💡', label: 'Newest & Recent'    },
              { key: 'top',        icon: '🏆', label: 'Popular of the day' },
              { key: 'unanswered', icon: '🔥', label: 'Unanswered'          },
            ].map(f => (
              <li
                key={f.key}
                className={`hf__filter-item${currentFilter === f.key ? ' is-active' : ''}`}
                onClick={() => this.setFilter(f.key)}
              >
                <span className="hf__fi-icon">{f.icon}</span>
                <span className="hf__fi-label">{f.label}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="hf__card">
          <div className="hf__card-hd">Categories</div>
          <div className="hf__cats">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                className={`hf__cat${activeCat === cat ? ' is-active' : ''}`}
                onClick={() => this.setCategory(cat)}
              >
                <span className="hf__cat-dot" style={{ background: CAT_COLORS[cat] || '#64748B' }} />
                <span className="hf__cat-name">{cat}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="hf__card">
          <div className="hf__card-hd">Popular Tags</div>
          <ul className="hf__tag-list">
            {POPULAR_TAGS.map((t, i) => (
              <li key={i} className="hf__tag-item">
                <span className="hf__tag-hash">#</span>
                <span className="hf__tag-nm">{t.name.replace('#', '')}</span>
                {t.trending && <span className="hf__trending">Trending</span>}
                <span className="hf__tag-posts">{t.posts} posts</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="hf__card">
          <div className="hf__card-hd">Pinned Groups</div>
          <ul className="hf__pinned-list">
            {PINNED_GROUPS.map((g, i) => (
              <li key={i} className="hf__pinned-item">
                <span className="hf__tag-hash">#</span>{g}
              </li>
            ))}
          </ul>
        </div>

        <div className="hf__card">
          <div className="hf__notice">
            <span className="hf__notice-icon">ℹ</span>
            <span>Peer support only — always consult a licensed healthcare provider.</span>
          </div>
        </div>
      </aside>
    );
  }

  renderPostCard(post) {
    const replyCount = post.commentCount || 0;
    return (
      <article key={post.id} className="hf__thread">
        {post.pinned && <div className="hf__pin" />}
        <div className="hf__thread-body">

          <div className="hf__thread-top">
            <div className="hf__badges">
              <span className={`hf__badge ${catBadgeClass(post.category)}`}>{post.category}</span>
              {(post.tags || []).map((t, i) => (
                <span key={i} className="hf__badge hf__badge-sl">{t}</span>
              ))}
              {post.answered && (
                <span className="hf__badge hf__badge-ok">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2 5l2.5 2.5L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Answered
                </span>
              )}
            </div>
            <span className="hf__thread-ts">{timeAgo(post.ts)}</span>
          </div>

          {post.imageUrl && <img src={post.imageUrl} alt="post" className="hf__thread-img" />}

          <div className="hf__thread-title" onClick={() => this.openDetail(post.id)}>
            {post.title}
          </div>
          <div className="hf__thread-body-txt">{post.body}</div>

          <div className="hf__thread-foot">
            <div className="hf__author">
              <div className="hf__av" style={{ width: 26, height: 26, fontSize: 10, background: post.avatarBg, color: post.avatarCol }}>
                {post.authorInitials}
              </div>
              <span className="hf__author-name">{post.author}</span>
              <span className={`hf__rpill ${rolePillClass(post.role)}`}>{post.role}</span>
            </div>

            <div className="hf__thread-stats">
              <button
                className={`hf__vbtn${post.liked ? ' is-voted' : ''}`}
                onClick={() => this.handleVote(post.id)}
              >
                <svg viewBox="0 0 13 13" fill="none">
                  <path d="M6.5 1.5l1.3 2.6 2.9.4-2.1 2 .5 2.9L6.5 8 4.4 9.4l.5-2.9L2.8 4.5l2.9-.4z" stroke="currentColor" strokeWidth="1.2" />
                </svg>
                {post.votes || 0}
              </button>

              <button className="hf__rbtn" onClick={() => this.toggleCommentInput(post.id)}>
                <svg viewBox="0 0 13 13" fill="none">
                  <path d="M1 6.5C1 3.46 3.46 1 6.5 1S12 3.46 12 6.5c0 1.5-.6 2.87-1.57 3.87L11 12l-2-.8A5.47 5.47 0 016.5 12C3.46 12 1 9.54 1 6.5z" stroke="currentColor" strokeWidth="1.2" />
                </svg>
                {replyCount} replies
              </button>

              <div className="hf__menu-wrap">
                <button className="hf__rbtn" onClick={() => this.toggleShareMenu(post.id)}>
                  <svg viewBox="0 0 13 13" fill="none">
                    <circle cx="10" cy="2.5" r="1.5" stroke="currentColor" strokeWidth="1.2" />
                    <circle cx="2.5" cy="6.5" r="1.5" stroke="currentColor" strokeWidth="1.2" />
                    <circle cx="10" cy="10.5" r="1.5" stroke="currentColor" strokeWidth="1.2" />
                    <path d="M4 5.8l4.5-2.5M4 7.2l4.5 2.5" stroke="currentColor" strokeWidth="1.2" />
                  </svg>
                  Share
                </button>
                {post.showShareMenu && (
                  <div className="hf__dropdown">
                    <button onClick={() => this.handleCopyLink(post.id)}>Copy Link</button>
                  </div>
                )}
              </div>

              <div className="hf__menu-wrap">
                <button className="hf__rbtn" onClick={() => this.toggleReportMenu(post.id)}>
                  <svg viewBox="0 0 13 13" fill="none">
                    <circle cx="6.5" cy="6.5" r="1" fill="currentColor" />
                    <circle cx="10" cy="6.5" r="1" fill="currentColor" />
                    <circle cx="3" cy="6.5" r="1" fill="currentColor" />
                  </svg>
                </button>
                {post.showReportMenu && (
                  <div className="hf__dropdown">
                    <button onClick={() => this.handleReport(post.id)}>Report</button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {post.showCommentInput && (
            <div className="hf__comments-section">
              <div className="hf__comment-inp-row">
                <input
                  type="text"
                  className="hf__comment-inp"
                  placeholder="Add a comment…"
                  value={post.commentText || ''}
                  onChange={(e) => this.handleCommentChange(post.id, e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && this.handleAddComment(post.id)}
                />
                <button className="hf__post-btn" onClick={() => this.handleAddComment(post.id)}>
                  Post
                </button>
              </div>
            </div>
          )}
        </div>
      </article>
    );
  }

  renderFeed() {
    const posts = this.getFilteredPosts();
    return (
      <main className="hf__feed">
        <div className="hf__feed-hd">
          <h1 className="hf__feed-title">
            {this.state.activeCat === 'All' ? 'Discussions' : this.state.activeCat}
          </h1>
          <div className="hf__filters">
            {[
              { key: 'latest',     label: 'Latest'     },
              { key: 'top',        label: 'Top'        },
              { key: 'unanswered', label: 'Unanswered' },
            ].map(f => (
              <button
                key={f.key}
                className={`hf__filter${this.state.currentFilter === f.key ? ' is-active' : ''}`}
                onClick={() => this.setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="hf__ask" onClick={this.openModal}>
          <div className="hf__av" style={{ width: 34, height: 34, background: 'linear-gradient(135deg,#93C5FD,#2563EB)', color: '#fff', fontSize: 12 }}>
            {this.state.user?.initials || 'ME'}
          </div>
          <span className="hf__ask-ph">Share a health question or start a discussion…</span>
          <button className="hf__ask-btn" onClick={(e) => { e.stopPropagation(); this.openModal(); }}>
            Ask Community
          </button>
        </div>

        {posts.length === 0 ? (
          <div className="hf__empty">
            <svg viewBox="0 0 44 44" fill="none">
              <circle cx="22" cy="22" r="20" stroke="currentColor" strokeWidth="1.5" />
              <path d="M15 22h14M22 15v14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <p>
              {this.state.searchQuery
                ? `No posts match "${this.state.searchQuery}"`
                : 'No posts in this category yet. Be the first!'}
            </p>
          </div>
        ) : (
          posts.map(p => this.renderPostCard(p))
        )}
      </main>
    );
  }

  renderRightSidebar() {
    const { chatMessages, chatInput, chatTyping, meetups, showAllMeetups, user } = this.state;
    const shownMeetups = showAllMeetups ? meetups : meetups.slice(0, 5);

    return (
      <aside className="hf__right">
        <div className="hf__spotlight">
          <div className="hf__spot-lbl">Weekly spotlight</div>
          <div className="hf__spot-title">Live Q&amp;A — Heart Health This Friday</div>
          <div className="hf__spot-sub">Dr. Kavitha Rao, Interventional Cardiologist, answers live at 6 PM IST.</div>
          <button className="hf__spot-cta">Register free →</button>
        </div>

        <div className="hf__chat">
          <div className="hf__chat-bar">
            <div className="hf__chat-bar-l">
              <span className="hf__chat-bar-title">Community Chat</span>
              <span className="hf__chat-live">
                <span className="hf__chat-pulse" />LIVE
              </span>
            </div>
            <span className="hf__chat-count">Online</span>
          </div>

          <div className="hf__chat-body" ref={this.chatBodyRef}>
            {chatMessages.map(m => {
              const isOwn = user && m.authorId === user.uid;
              return (
                <div key={m.id} className={`hf__msg${isOwn ? ' hf__msg-own' : ''}`}>
                  <div className="hf__av hf__msg-av" style={{ background: m.bg, color: m.col }}>
                    {m.init}
                  </div>
                  <div className="hf__msg-bubble">
                    {!isOwn && (
                      <div className="hf__msg-who">
                        {m.from}
                        {m.isDoc && <span className="hf__msg-dr">Doctor</span>}
                      </div>
                    )}
                    <div className="hf__msg-txt">{m.text}</div>
                    <div className="hf__msg-time">
                      {m.ts?.toDate ? m.ts.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                    </div>
                  </div>
                </div>
              );
            })}
            {chatTyping && (
              <div className="hf__typing">
                <div className="hf__av hf__msg-av" style={{ background: '#DBEAFE', color: '#1D4ED8' }}>...</div>
                <div className="hf__typing-dots"><span /><span /><span /></div>
              </div>
            )}
          </div>

          <div className="hf__chat-foot">
            <input
              className="hf__chat-inp"
              type="text"
              placeholder="Type a message…"
              value={chatInput}
              onChange={this.handleChatInput}
              onKeyDown={this.handleChatKeyDown}
            />
            <button className="hf__chat-send" onClick={this.handleChatSend}>
              <svg viewBox="0 0 15 15" fill="none">
                <path d="M2 7.5h11M8.5 3L13 7.5 8.5 12" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>

        <div className="hf__card">
          <div className="hf__card-hd">Community Stats</div>
          <div className="hf__stat-grid">
            {STATS.map((s, i) => (
              <div key={i} className="hf__stat-cell">
                <div className="hf__stat-num">{s.num}</div>
                <div className="hf__stat-lbl">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="hf__card">
          <div className="hf__card-hd">Active Specialists</div>
          <div className="hf__spec-list">
            {SPECIALISTS.map((s, i) => (
              <div key={i} className="hf__spec-row">
                <div className="hf__spec-av" style={{ background: s.bg, color: s.col }}>{s.initials}</div>
                <div className="hf__spec-info">
                  <div className="hf__spec-name">{s.name}</div>
                  <div className="hf__spec-role">{s.spec}</div>
                </div>
                <div className={s.online ? 'hf__dot-on' : 'hf__dot-off'} />
              </div>
            ))}
          </div>
        </div>

        <div className="hf__card">
          <div className="hf__card-hd">Meetups</div>
          <ul className="hf__meetup-list">
            {shownMeetups.map((m, i) => (
              <li key={i} className="hf__meetup-item">
                <span className="hf__meetup-date">{m.date}</span>
                <div className="hf__meetup-info">
                  <p className="hf__meetup-title">{m.title}</p>
                  <p className="hf__meetup-loc">{m.location}</p>
                </div>
              </li>
            ))}
          </ul>
          {meetups.length > 5 && (
            <button
              className="hf__read-more-btn"
              onClick={() => this.setState(prev => ({ showAllMeetups: !prev.showAllMeetups }))}
            >
              {showAllMeetups ? 'Show Less' : 'Read More'}
            </button>
          )}
        </div>
      </aside>
    );
  }

  renderNewPostModal() {
    if (!this.state.showModal) return null;
    const { newPostTitle, newPostBody, newPostTags, newPostCat, newPostRole, newPostImage, postUploading } = this.state;

    return (
      <div
        className="hf__overlay is-open"
        onClick={(e) => e.target.className.includes('hf__overlay') && this.closeModal()}
      >
        <div className="hf__modal">
          <div className="hf__modal-hd">
            <div className="hf__modal-title">Create New Post</div>
            <button className="hf__modal-x" onClick={this.closeModal}>✕</button>
          </div>

          <div className="hf__fg">
            <label>Post Title *</label>
            <input
              className="hf__fi"
              maxLength={120}
              placeholder="Describe your question or topic clearly…"
              value={newPostTitle}
              onChange={(e) => this.setState({ newPostTitle: e.target.value })}
            />
            <div className={`hf__cc${newPostTitle.length > 100 ? ' warn' : ''}`}>{newPostTitle.length} / 120</div>
          </div>

          <div className="hf__fg">
            <label>Details</label>
            <textarea
              className="hf__fi hf__fi-ta"
              maxLength={1000}
              placeholder="Share background, what you've tried, what you're looking for…"
              value={newPostBody}
              onChange={(e) => this.setState({ newPostBody: e.target.value })}
            />
            <div className={`hf__cc${newPostBody.length > 900 ? ' warn' : ''}`}>{newPostBody.length} / 1000</div>
          </div>

          <div className="hf__fg-row">
            <div className="hf__fg">
              <label>Category *</label>
              <select
                className="hf__fi hf__fi-sel"
                value={newPostCat}
                onChange={(e) => this.setState({ newPostCat: e.target.value })}
              >
                {CATEGORIES.slice(1).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="hf__fg">
              <label>Your Role</label>
              <select
                className="hf__fi hf__fi-sel"
                value={newPostRole}
                onChange={(e) => this.setState({ newPostRole: e.target.value })}
              >
                {['Patient', 'Doctor', 'Caregiver', 'Researcher'].map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="hf__fg">
            <label>Tags <span style={{ fontWeight: 400, color: '#7A9CC8' }}>(comma separated)</span></label>
            <input
              className="hf__fi"
              placeholder="e.g. diabetes, diet, insulin"
              value={newPostTags}
              onChange={(e) => this.setState({ newPostTags: e.target.value })}
            />
          </div>

          <div className="hf__fg">
            <label>Image <span style={{ fontWeight: 400, color: '#7A9CC8' }}>(optional)</span></label>
            <div className="hf__img-upload-row">
              <label className="hf__img-upload-btn" htmlFor="hf-post-img">
                {newPostImage ? '✓ Image selected' : '+ Upload Image'}
              </label>
              <input
                id="hf-post-img"
                type="file" accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => this.handleNewPostImageChange(e.target.files[0])}
              />
              {newPostImage && <img src={newPostImage} alt="preview" className="hf__img-preview" />}
            </div>
          </div>

          <div className="hf__modal-ft">
            <button className="hf__btn-cancel" onClick={this.closeModal} disabled={postUploading}>Cancel</button>
            <button className="hf__btn-post" onClick={this.handleCreatePost} disabled={postUploading}>
              {postUploading ? 'Publishing…' : 'Publish Post'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderThreadDetail() {
    const { detailPostId, replyText, detailComments } = this.state;
    if (!detailPostId) return null;
    const post = this.state.posts.find(p => p.id === detailPostId);
    if (!post) return null;

    const topLevel = detailComments.filter(c => !c.parentId);

    return (
      <div
        className="hf__detail-ov is-open"
        onClick={(e) => e.target.className.includes('hf__detail-ov') && this.closeDetail()}
      >
        <div className="hf__detail-box">
          <div className="hf__det-hd">
            <div className="hf__det-hd-info">
              <div className="hf__badges">
                <span className={`hf__badge ${catBadgeClass(post.category)}`}>{post.category}</span>
                {(post.tags || []).map((t, i) => <span key={i} className="hf__badge hf__badge-sl">{t}</span>)}
                {post.answered && (
                  <span className="hf__badge hf__badge-ok">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M2 5l2.5 2.5L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Answered
                  </span>
                )}
              </div>
              <div className="hf__det-title">{post.title}</div>
              <div className="hf__det-meta">
                <div className="hf__av" style={{ width: 26, height: 26, fontSize: 10, background: post.avatarBg, color: post.avatarCol }}>
                  {post.authorInitials}
                </div>
                <span style={{ fontSize: 12, fontWeight: 500, color: '#3A5694' }}>{post.author}</span>
                <span className={`hf__rpill ${rolePillClass(post.role)}`}>{post.role}</span>
                <span style={{ fontSize: 11, color: '#7A9CC8' }}>{timeAgo(post.ts)}</span>
              </div>
            </div>
            <button className="hf__det-close" onClick={this.closeDetail}>✕</button>
          </div>

          <div className="hf__det-body">
            {post.imageUrl && <img src={post.imageUrl} alt="post" className="hf__thread-img" style={{ marginBottom: 16 }} />}
            <div className="hf__det-text">{post.body}</div>

            <div className="hf__det-actions">
              <button
                className={`hf__vbtn${post.liked ? ' is-voted' : ''}`}
                onClick={this.handleDetailVote}
              >
                <svg viewBox="0 0 13 13" fill="none">
                  <path d="M6.5 1.5l1.3 2.6 2.9.4-2.1 2 .5 2.9L6.5 8 4.4 9.4l.5-2.9L2.8 4.5l2.9-.4z" stroke="currentColor" strokeWidth="1.2" />
                </svg>
                {post.votes || 0} upvotes
              </button>
              <button className="hf__rbtn">
                <svg viewBox="0 0 13 13" fill="none">
                  <path d="M1 6.5C1 3.46 3.46 1 6.5 1S12 3.46 12 6.5c0 1.5-.6 2.87-1.57 3.87L11 12l-2-.8A5.47 5.47 0 016.5 12C3.46 12 1 9.54 1 6.5z" stroke="currentColor" strokeWidth="1.2" />
                </svg>
                {detailComments.length} replies
              </button>
            </div>

            <div className="hf__rep-title">Replies</div>
            <div className="hf__rep-list">
              {topLevel.length === 0
                ? <p style={{ fontSize: 13, color: '#7A9CC8', padding: '8px 0' }}>No replies yet — be the first!</p>
                : topLevel.map(c => this.renderComment(c, detailComments))
              }
            </div>

            <div className="hf__replybox">
              <textarea
                className="hf__reply-ta"
                placeholder="Write your reply…"
                value={replyText}
                onChange={(e) => this.setState({ replyText: e.target.value })}
              />
              <button className="hf__reply-go" onClick={this.handleDetailReply}>Reply</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────
  render() {
    if (!this.state.loggedIn) {
      return (
        <LoginScreen
          onGoogleLogin={this.handleGoogleLogin}
          onEmailLogin={this.handleEmailLogin}
          onEmailSignup={this.handleEmailSignup}
        />
      );
    }
    return (
      <div className="hf__root">
        {this.renderHeader()}
        <div className="hf__page">
          {this.renderLeftSidebar()}
          {this.renderFeed()}
          {this.renderRightSidebar()}
        </div>
        {this.renderNewPostModal()}
        {this.renderThreadDetail()}
      </div>
    );
  }
}

export default ForumLayout;